// Quotes (P2.4a): build a draft (lines, currency, expiry, terms version and
// whether a replacement needs the client's approval — no default, you decide),
// issue it, then record the client's acceptance with their evidence (D).
// Totals are always the server's. Issuing a new version supersedes the previous
// one and invalidates its recorded acceptance (commerce).
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  type Booking, type Quote, type QuoteDraft, type QuoteLine, type Terms, createQuote, issueQuote, rejectQuote, updateQuote,
} from '@/services/baBookingService';
import { formatInZone, formatMinor, isoToZonedLocal, minorToMajorText, parseMajorToMinor, zonedLocalToIso } from './bookingFormat';
import { RecordClientAcceptanceForm } from './EvidenceForms';
import { Field, type K, NativeSelect, StatusBadge, useRunAction } from './shared';

type LineForm = { kind: QuoteLine['kind']; description: string; quantity: string; unit: string };

const LINE_KINDS: { v: QuoteLine['kind']; label: string }[] = [
  { v: 'worker_service', label: 'Ambassador service' },
  { v: 'management_fee', label: 'Management fee' },
  { v: 'expense', label: 'Expense' },
  { v: 'tax', label: 'Tax' },
];

export const QuoteDraftForm: React.FC<{
  booking: Booking; k: K; activeTerms: Terms[]; existing?: Quote; onSaved: (q: Quote) => void; onCancel?: () => void;
}> = ({ booking, k, activeTerms, existing, onSaved, onCancel }) => {
  const tz = booking.timezone;
  const { busy, run } = useRunAction(k);
  const [currency, setCurrency] = useState(existing?.currency ?? 'TZS');
  const [lines, setLines] = useState<LineForm[]>(() => (existing?.lines?.length
    ? existing.lines.map((l) => ({ kind: l.kind, description: l.description, quantity: String(l.quantity), unit: minorToMajorText(l.unitAmountMinor, existing.currency) }))
    : [{ kind: 'worker_service', description: '', quantity: '1', unit: '' }]));
  const [expires, setExpires] = useState(isoToZonedLocal(existing?.expiresAt, tz));
  const [termsId, setTermsId] = useState(existing?.termsVersionId ?? '');
  // No default (commerce: "you decide"): '' until chosen.
  const [repl, setRepl] = useState<'' | 'yes' | 'no'>(existing ? (existing.replacementNeedsClientApproval ? 'yes' : 'no') : '');

  const parsed = lines.map((l) => ({
    kind: l.kind, description: l.description.trim(), quantity: Number(l.quantity),
    unitAmountMinor: parseMajorToMinor(l.unit, currency),
  }));
  const linesOk = parsed.length > 0 && parsed.every((l) => l.description && Number.isInteger(l.quantity) && l.quantity > 0 && l.unitAmountMinor != null);
  const ready = linesOk && /^[A-Za-z]{3}$/.test(currency) && repl !== '';
  const setLine = (i: number, patch: Partial<LineForm>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    const body: QuoteDraft = {
      currency: currency.toUpperCase(),
      lines: parsed.map((l) => ({ kind: l.kind, description: l.description, quantity: l.quantity, unitAmountMinor: l.unitAmountMinor as number })),
      replacementNeedsClientApproval: repl === 'yes',
      ...(expires ? { expiresAt: zonedLocalToIso(expires, tz) } : {}),
      ...(termsId ? { termsVersionId: termsId } : {}),
    };
    const out = await run(() => (existing ? updateQuote(existing.id, existing.version, body) : createQuote(booking.id, body)),
      k('quote.saved', 'Quote draft saved.'));
    if (out) onSaved(out);
  };

  const qid = existing?.id ?? 'new';
  return (
    <form onSubmit={save} className="space-y-3 rounded-md border border-border p-3" data-testid="quote-form">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label={k('quote.currency', 'Currency')} htmlFor={`q-cur-${qid}`}>
          <Input id={`q-cur-${qid}`} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value)} required />
        </Field>
        <Field label={k('quote.expires', 'Quote expires (needed to issue)')} htmlFor={`q-exp-${qid}`}>
          <Input id={`q-exp-${qid}`} type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
        <Field label={k('quote.terms', 'Cancellation and no-show terms')} htmlFor={`q-terms-${qid}`}
          hint={activeTerms.length === 0 ? k('confirm.no_active_terms', 'Waiting for Fern’s cancellation and no-show terms') : undefined}>
          <NativeSelect id={`q-terms-${qid}`} value={termsId} onChange={(e) => setTermsId(e.target.value)} disabled={activeTerms.length === 0}>
            <option value="">{k('common.choose', 'Choose…')}</option>
            {activeTerms.map((t) => <option key={t.id} value={t.id}>{`${t.title} (v${t.versionNo})`}</option>)}
          </NativeSelect>
        </Field>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-foreground">{k('quote.lines', 'Lines')}</legend>
        {lines.map((l, i) => (
          <div key={i} className="grid gap-2 md:grid-cols-[10rem_1fr_6rem_9rem_auto] items-end">
            <NativeSelect aria-label={k('quote.line_kind', 'Kind')} value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value as QuoteLine['kind'] })}>
              {LINE_KINDS.map((x) => <option key={x.v} value={x.v}>{k(`quote.kind_${x.v}`, x.label)}</option>)}
            </NativeSelect>
            <Input aria-label={k('quote.line_description', 'Description')} placeholder={k('quote.line_description', 'Description')}
              value={l.description} maxLength={300} onChange={(e) => setLine(i, { description: e.target.value })} />
            <Input aria-label={k('quote.line_quantity', 'Quantity')} inputMode="numeric" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
            <Input aria-label={k('quote.line_unit', 'Unit amount')} placeholder={k('quote.line_unit', 'Unit amount')} inputMode="decimal"
              value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} />
            <Button type="button" variant="ghost" size="sm" aria-label={k('quote.remove_line', 'Remove line')} disabled={lines.length === 1}
              onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { kind: 'worker_service', description: '', quantity: '1', unit: '' }])}>
          <Plus className="w-4 h-4 mr-1" />{k('quote.add_line', 'Add line')}
        </Button>
      </fieldset>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium text-foreground">{k('quote.replacement', 'If an ambassador is replaced, does the client have to approve the replacement?')}</legend>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name={`q-repl-${qid}`} checked={repl === 'yes'} onChange={() => setRepl('yes')} />
          {k('quote.replacement_yes', 'Yes, the client approves each replacement')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name={`q-repl-${qid}`} checked={repl === 'no'} onChange={() => setRepl('no')} />
          {k('quote.replacement_no', 'No')}
        </label>
      </fieldset>
      <p className="text-xs text-muted-foreground">{k('quote.totals_note', 'Totals are calculated by KaziPeople when you save.')}</p>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !ready}>{k('quote.save', 'Save quote draft')}</Button>
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>{k('common.cancel', 'Cancel')}</Button>}
      </div>
    </form>
  );
};

export const QuoteCard: React.FC<{
  booking: Booking; quote: Quote; k: K; activeTerms: Terms[]; onChange: () => void;
}> = ({ booking, quote: q, k, activeTerms, onChange }) => {
  const prompt = usePrompt();
  const { busy, run } = useRunAction(k);
  const [editing, setEditing] = useState(false);
  const tz = booking.timezone;

  const issue = async () => {
    const out = await run((key) => issueQuote(q.id, { version: q.version, key }), k('quote.issued', 'Quote issued.'));
    if (out) onChange();
  };
  const reject = async () => {
    const reason = await prompt({
      title: k('quote.reject_title', 'Record that the client rejected this quote'),
      label: k('quote.reject_reason', 'What did the client say?'), required: true, multiline: true,
      confirmLabel: k('quote.reject_confirm', 'Record client rejection'),
    });
    if (reason == null || !reason.trim()) return;
    const out = await run((key) => rejectQuote(q.id, reason.trim(), { version: q.version, key }), k('quote.rejected', 'Client rejection recorded.'));
    if (out) onChange();
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid={`quote-${q.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{k('quote.version', 'Quote v{{n}}', { n: q.versionNo })}</span>
        {q.status === 'accepted'
          ? <StatusBadge status="accepted" k={k} label={k('quote.accepted_badge', 'Client acceptance recorded')} />
          : <StatusBadge status={q.status} k={k} />}
        <span className="text-sm text-foreground tabular-nums">{formatMinor(q.totalMinor, q.currency)}</span>
        {q.expiresAt && <span className="text-xs text-muted-foreground">{k('quote.expires_at', 'Expires {{at}}', { at: formatInZone(q.expiresAt, tz) })}</span>}
      </div>
      {!editing && (
        <table className="w-full text-sm">
          <tbody>
            {q.lines.map((l, i) => (
              <tr key={i} className="border-t border-border">
                <td className="py-1 pr-2 text-muted-foreground">{k(`quote.kind_${l.kind}`, LINE_KINDS.find((x) => x.v === l.kind)?.label ?? l.kind)}</td>
                <td className="py-1 pr-2 text-foreground">{l.description}</td>
                <td className="py-1 pr-2 tabular-nums">{l.quantity} × {formatMinor(l.unitAmountMinor, q.currency)}</td>
                <td className="py-1 text-right tabular-nums">{formatMinor(l.amountMinor ?? l.quantity * l.unitAmountMinor, q.currency)}</td>
              </tr>
            ))}
            <tr className="border-t border-border">
              <td colSpan={3} className="py-1 text-muted-foreground">{k('quote.tax', 'Tax')}</td>
              <td className="py-1 text-right tabular-nums">{formatMinor(q.taxMinor, q.currency)}</td>
            </tr>
            <tr className="border-t border-border font-semibold">
              <td colSpan={3} className="py-1">{k('quote.total', 'Total')}</td>
              <td className="py-1 text-right tabular-nums">{formatMinor(q.totalMinor, q.currency)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <p className="text-xs text-muted-foreground">
        {q.replacementNeedsClientApproval
          ? k('quote.replacement_needs', 'Replacements need the client’s approval.')
          : k('quote.replacement_free', 'Replacements don’t need the client’s approval.')}
      </p>

      {q.status === 'draft' && !editing && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(true)}>{k('quote.edit', 'Edit draft')}</Button>
          <Button size="sm" disabled={busy || !q.expiresAt} onClick={issue}>{k('quote.issue', 'Issue quote')}</Button>
          {!q.expiresAt && <span className="text-xs text-muted-foreground self-center">{k('quote.need_expiry', 'Set an expiry before issuing.')}</span>}
        </div>
      )}
      {q.status === 'draft' && editing && (
        <QuoteDraftForm booking={booking} k={k} activeTerms={activeTerms} existing={q}
          onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />
      )}
      {q.status === 'issued' && booking.status === 'quoting' && (
        <>
          <RecordClientAcceptanceForm quote={q} booking={booking} k={k} onDone={() => onChange()} />
          <Button size="sm" variant="outline" disabled={busy} onClick={reject}>{k('quote.reject_confirm', 'Record client rejection')}</Button>
        </>
      )}
      {q.acceptance && (
        <div className="rounded-md bg-muted p-2 text-sm" data-testid="acceptance-record">
          <p className="text-foreground">
            {k('acceptance.recorded', 'Client acceptance recorded: {{name}} accepted on {{date}}.', {
              name: q.acceptance.clientPersonName, date: formatInZone(q.acceptance.clientAcceptedAt, tz),
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {k('acceptance.recorded_meta', 'Evidence: {{kind}} · recorded {{at}}', {
              kind: k(`acceptance.kind_${q.acceptance.evidenceKind}`, q.acceptance.evidenceKind.replace(/_/g, ' ')),
              at: formatInZone(q.acceptance.recordedAt, tz),
            })}
          </p>
          {q.acceptance.invalidatedAt && (
            <p className="text-xs text-danger-strong">{k('acceptance.invalidated', 'No longer valid: a newer quote version was issued.')}</p>
          )}
        </div>
      )}
    </div>
  );
};
